import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import {
  getPlan,
  createPlan,
  updatePlan,
  submitPlan,
  listMySkills,
} from "../../api/advisorApi";

// /advisor/plans/new  → create
// /advisor/plans/:id  → edit

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5">
      {label}
    </div>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </label>
);

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500";

const CURRENCIES = ["USD", "INR", "EUR", "GBP", "AED", "SGD", "AUD", "CAD"];

const Banner = ({ kind, children }) => {
  const cls =
    kind === "error"
      ? "bg-red-500/10 text-red-500 border-red-500/40"
      : "bg-emerald-500/10 text-emerald-500 border-emerald-500/40";
  const Icon = kind === "error" ? ExclamationTriangleIcon : CheckCircleIcon;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${cls}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  );
};

const PlanEditor = () => {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null);

  const [plan, setPlan] = useState({
    name: "",
    summary: "",
    price_minor_units: 0,
    currency: "USD",
    billing_interval: "month",
    skills: [],
    review_status: "draft",
  });
  // Major-unit input for human friendliness ("19.00" instead of 1900).
  const [priceMajor, setPriceMajor] = useState("");
  const [availableSkills, setAvailableSkills] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const skills = await listMySkills();
        setAvailableSkills(skills);
        if (!isCreate) {
          const p = await getPlan(id);
          setPlan(p);
          setPriceMajor(((p.price_minor_units || 0) / 100).toFixed(2));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isCreate]);

  const updateLocal = (k, v) => setPlan((p) => ({ ...p, [k]: v }));

  const flash = (kind, message) => {
    setBanner({ kind, message });
    setTimeout(() => setBanner(null), 4000);
  };

  const buildPayload = () => {
    const major = parseFloat(priceMajor || "0") || 0;
    return {
      name: plan.name,
      summary: plan.summary || "",
      price_minor_units: Math.round(major * 100),
      currency: plan.currency,
      billing_interval: plan.billing_interval,
      skills: plan.skills || [],
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isCreate) {
        const created = await createPlan(buildPayload());
        navigate(`/advisor/plans/${created.id}`, { replace: true });
        flash("success", "Saved.");
      } else {
        const updated = await updatePlan(id, buildPayload());
        setPlan(updated);
        flash("success", "Saved.");
      }
    } catch (err) {
      const data = err?.response?.data || {};
      const firstKey = Object.keys(data)[0];
      const msg = firstKey
        ? `${firstKey}: ${Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey]}`
        : "Could not save.";
      flash("error", msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (isCreate) return;
    setSubmitting(true);
    try {
      const updated = await submitPlan(id);
      setPlan(updated);
      flash("success", "Submitted for review.");
    } catch (err) {
      const data = err?.response?.data || {};
      flash("error", data.detail || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSkill = (skillId) =>
    setPlan((p) => {
      const have = p.skills || [];
      return {
        ...p,
        skills: have.includes(skillId)
          ? have.filter((x) => x !== skillId)
          : [...have, skillId],
      };
    });

  if (loading) return <Card>Loading…</Card>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{isCreate ? "New plan" : plan.name || "Plan"}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          A plan is what subscribers buy. They get every skill bundled in, applied
          by the AI agents.
        </p>
      </div>

      {banner && <Banner kind={banner.kind}>{banner.message}</Banner>}

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Plan name" hint="Shown to subscribers on the marketplace card.">
            <input
              className={inputCls}
              value={plan.name || ""}
              onChange={(e) => updateLocal("name", e.target.value)}
              maxLength={120}
              placeholder="e.g. Growth Investor Plan"
            />
          </Field>
          <Field label="Billing interval">
            <select
              className={inputCls}
              value={plan.billing_interval}
              onChange={(e) => updateLocal("billing_interval", e.target.value)}
            >
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Field label="Price" hint="In the chosen currency. Tax/fees handled at checkout.">
            <input
              className={inputCls}
              inputMode="decimal"
              value={priceMajor}
              onChange={(e) => setPriceMajor(e.target.value)}
              placeholder="19.00"
            />
          </Field>
          <Field label="Currency">
            <select
              className={inputCls}
              value={plan.currency}
              onChange={(e) => updateLocal("currency", e.target.value)}
            >
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Summary" hint="One sentence on the plan card.">
            <input
              className={inputCls}
              value={plan.summary || ""}
              onChange={(e) => updateLocal("summary", e.target.value)}
              maxLength={300}
              placeholder="e.g. Long-term growth strategy with tax overlay."
            />
          </Field>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-bold mb-2">Included skills</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Pick the skills this plan should bundle. Only your own skills are eligible.
          Draft skills can be included while the plan itself is in draft; they go live
          together at review approval.
        </p>
        {availableSkills.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            You haven't authored any skills yet. <a className="text-indigo-500 underline" href="/advisor/skills/new">Create one first</a>.
          </p>
        ) : (
          <div className="space-y-2">
            {availableSkills.map((s) => {
              const on = (plan.skills || []).includes(s.id);
              return (
                <label key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${on ? "border-indigo-500 bg-indigo-500/5" : "border-gray-300 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/5"}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleSkill(s.id)}
                    className="w-4 h-4"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{s.name}</p>
                    {s.summary && (
                      <p className="text-xs text-gray-500 truncate">{s.summary}</p>
                    )}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-500">
                    {s.status} · risk: {s.risk_band}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </Card>

      <div className="flex flex-wrap gap-3 justify-end">
        <button
          type="button"
          onClick={() => navigate("/advisor/plans")}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-white/15 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !plan.name?.trim()}
          className="px-4 py-2 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-sm font-semibold hover:bg-indigo-500/20 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save draft"}
        </button>
        {!isCreate && plan.review_status === "draft" && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit for review"}
          </button>
        )}
      </div>
    </div>
  );
};

export default PlanEditor;
